import { stateSetter } from '@/utils';
import { IServerColumnConfig } from '@/components/table/column-config';

export type FeedbackStatus =
	| 'new'
	| 'in progress'
	| 'resolved'
	| 'rejected'
	| 'future reference'
	| string;

export type FeedbackType = 'compliment' | 'bug' | 'suggestion' | 'support';

// Valoarea EXACTĂ a parametrului `Type` cerută de BE (egalitate case+diacritice-sensibilă,
// FeedbackRepository.cs: f.Type == queryDto.Type). Orice abatere → 0 rezultate.
// `support` = „Contact echipa de suport" (C mare, restul litere mici, fără diacritice, un
// singur „p", de cu literă mică).
export const FEEDBACK_TYPE_PARAM: Record<FeedbackType, string> = {
	bug: 'Bug',
	compliment: 'Compliment',
	suggestion: 'Sugestie',
	support: 'Contact echipa de suport'
};

export interface IFeedbackUser {
	id: number;
	firstName: string;
	lastName: string;
	email?: string;
	countryCode?: string;
	phone?: string;
}

export interface IFeedbackStatusInfo {
	id: number;
	name: string;
}

interface IFeedbackAuthor {
	id: number;
	firstName: string;
	lastName: string;
	role: string;
	avatar: string;
}

export interface IFeedbackAsset {
	id: number;
	fileName: string;
	originalName: string;
	mimeType: string;
	extension: string;
	sizeBytes: number;
	publicUrl: string;
	type: string;
	width?: number;
	height?: number;
	pageCount?: number;
}

export interface IStatusLog {
	author: IFeedbackAuthor;
	oldStatus: IFeedbackStatusInfo | FeedbackStatus;
	newStatus: IFeedbackStatusInfo | FeedbackStatus;
	date: string;
}

interface IFeedbackLogLookup {
	id: number;
	name: string;
}

interface IFeedbackLogPerformer {
	id: number;
	firstName: string;
	lastName: string;
}

export interface IFeedbackLog {
	id: number;
	feedbackId: number;
	action: IFeedbackLogLookup;
	description: string;
	type: string;
	requestId: string;
	performedAt: string;
	performedBy?: IFeedbackLogPerformer;
}

export interface IFeedbackLogQuery {
	ActionId?: number;
	Type?: string;
	RequestId?: string;
	OrderBy?: string;
	Ascending?: boolean;
}

export interface IFeedback {
	id: number;
	user: IFeedbackUser;
	type: FeedbackType | string;
	status: IFeedbackStatusInfo | FeedbackStatus;
	description: string;
	deviceOs?: string | null;
	deviceOsVersion?: string | null;
	deviceScreenResolution?: string | null;
	deviceBrand?: string | null;
	deviceModel?: string | null;
	deviceRamSize?: string | null;
	deviceBatteryPercentage?: string | null;
	deviceReleaseHash?: string | null;
	assets?: IFeedbackAsset[];
	/** Numărul de imagini atașate (DTO-ul de listă nu trimite `assets`, doar contorul). */
	imageCount?: number;
	image?: string;
	deleted?: boolean;
	statusLogs?: IStatusLog[];
	createdAt: string;
	updatedAt?: string;
}

export const getFeedbackStatusName = (
	status?: IFeedback['status'] | IStatusLog['oldStatus'] | IStatusLog['newStatus']
): FeedbackStatus => {
	if (!status) return '';

	if (typeof status === 'string') return status;

	return status.name || '';
};

export const getFeedbackStatusId = (
	status?: IFeedback['status'] | IStatusLog['oldStatus'] | IStatusLog['newStatus']
): number | undefined => {
	if (!status || typeof status === 'string') return undefined;

	return status.id || undefined;
};

export const getFeedbackUserName = (user?: IFeedbackUser | null): string => {
	if (!user) return 'N/A';

	return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A';
};

export interface FeedbackLogsReturnType<T extends IFeedbackLog | IFeedbackLog[]> {
	data: T;
	setData: stateSetter<T>;
	loading: boolean;
	getLogs: (
		id: number | string,
		pageParam: number,
		itemsParam: number,
		queryParam?: IFeedbackLogQuery
	) => Promise<void>;
	page: number;
	items: number;
	setPage: stateSetter<number>;
	setItems: stateSetter<number>;
	totalPages: number;
	totalItems: number;
}

export interface FeedbackReturnType<T extends IFeedback | IFeedback[]> {
	data: T;
	setData: stateSetter<T>;
	loading: boolean;
	loadingCTA: boolean;
	/** Config-ul de coloane din BE (api/feedbacks/table-config); null → coloanele locale. */
	tableConfig: IServerColumnConfig[] | null;
	loadingTableConfig: boolean;
	getFeedbacks: (
		type: FeedbackType,
		pageParam: number,
		itemsParam: number
	) => Promise<void>;
	editFeedback: (
		id: number,
		statusId: number,
		onSuccess?: () => void
	) => Promise<void>;
	removeFeedback: (feedbackId: number, onSuccess?: () => void) => Promise<void>;
	page: number;
	items: number;
	setPage: stateSetter<number>;
	setItems: stateSetter<number>;
	totalPages: number;
	totalItems: number;
}
